import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { Destination as DestinationGraphQL, UpdateDestinationInput } from '@ui/__generated__/graphql'
import { ApolloErrorMessage } from '@ui/components/core/ApolloErrorMessage'
import { showToast } from '@ui/components/elements/ToastManager'
import { Spinner } from '@ui/components/loaders'
import { DynamicFormValues, Template } from '@ui/interfaces'
import { GetDestinationQuery, UpdateDestinationQuery } from '@ui/loaders/destinations'
import { ExporterForm } from '@ui/pages/pipelinesWorkflow/components/DestinationForms/ExporterForm'
import { useAuth } from '@ui/providers/AuthProvider.tsx'
import { useNavigationStateStore } from '@ui/stores'

const NameInput = ({ onChange, value }: { onChange: (val: string) => void; value: string }) => {
  return (
    <div className="mb-6">
      <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
        Name*
      </label>
      <div className="mt-2">
        <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-600 sm:max-w-md">
          <input
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            onChange={(e) => onChange(e.target.value)}
            placeholder="My destination"
            value={value}
          />
        </div>
      </div>
    </div>
  )
}

// TODO: let's consolidate this with <NewDestination />
export const Destination = () => {
  const { destinationId } = useParams()
  const { isDemoMode } = useAuth()

  const navigate = useNavigate()
  const { instanceId } = useNavigationStateStore(useShallow((state) => state.params))

  const [name, setName] = useState('')

  const queryResult = GetDestinationQuery(destinationId)

  const [updateDestination, { loading: loadingUpdateDestination, error: updateDestinationApolloErrorMessage }] =
    UpdateDestinationQuery()

  useEffect(() => {
    setName(queryResult.data?.destination?.name || '')
  }, [queryResult.data])

  const returnToDestinations = () => navigate(`/instances/${instanceId}/destinations`)

  const onSave = async (formData: DynamicFormValues) => {
    if (isDemoMode) return returnToDestinations()
    if (loadingUpdateDestination) return

    const input: UpdateDestinationInput = {
      id: destinationId as string,
      name,
      formData,
    }

    await updateDestination({ variables: { input } })

    showToast.success('Destination has been saved')

    returnToDestinations()
  }

  if (queryResult.loading) {
    return <Spinner />
  }

  if (queryResult.error) {
    return <ApolloErrorMessage error={queryResult.error} />
  }

  const destination: DestinationGraphQL = queryResult.data.destination

  return (
    <div className="p-4">
      <h1 className="mb-4 text-xl font-medium">Configuration</h1>
      <ExporterForm
        template={destination.template as unknown as Template}
        config={destination.formData}
        saveConfig={(values: DynamicFormValues) => {
          onSave(values)
        }}
        cancel={returnToDestinations}
        additionalInputs={<NameInput onChange={setName} value={name} />}
      />
      {updateDestinationApolloErrorMessage && (
        <p className="text-xs italic text-red-500">{updateDestinationApolloErrorMessage.message}</p>
      )}
    </div>
  )
}
